import { useEffect, useState } from 'react';
import {
  useAddKnowledgeBaseDocumentMutation,
  useAddKnowledgeBaseFileMutation,
  useDeleteKnowledgeBaseFilesMutation,
  useGetKnowledgeBaseByIdQuery,
  useGetKnowledgeBaseCollectorsQuery,
  useGetKnowledgeBaseFilesQuery,
  useLazyGetKnowledgeBaseFileByIdQuery,
  useLazyGetKnowledgeBaseFilesQuery,
  useUpdateKnowledgeBaseFileMutation,
  useUpdateKnowledgeBaseFilesMutation,
} from 'src/services/knowledgeBaseService';
import { API_VERSION } from 'src/constants/api-routes';
import { useNotification } from 'src/hooks/useNotification';
import { FILE_STATE } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import type { Integration, KBFile } from 'src/utils/fileUtils';
import { getApiVersion } from 'src/utils/apiVersion';
import { useIsCosupportManager } from 'src/hooks/useIsCosupportManager';

export interface FileDownloadDetails {
  filename: string;
  url: string;
  path: string;
}

interface LazyFilesArgs {
  id: string;
  collector_id: string;
  sync: boolean;
}

export const useKnowledgeBaseData = (
  knowledgeBaseId: string | undefined,
  skipRefetchFiles: boolean,
) => {
  const notification = useNotification();

  const [files, setFiles] = useState<KBFile[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);

  const isCosupportManager = useIsCosupportManager();
  const version = getApiVersion();
  const isV1 = version === API_VERSION.V1;

  const { data: currentKnowledgeBase } = useGetKnowledgeBaseByIdQuery({
    id: knowledgeBaseId,
    load_projects: true,
  });

  const { data: collectorsResponse, isLoading: isLoadingCollectors } =
    useGetKnowledgeBaseCollectorsQuery(
      { id: knowledgeBaseId, include_data: true },
      { skip: !knowledgeBaseId },
    );

  const {
    data: filesResponse,
    isLoading: isLoadingFiles,
    isSuccess: isSuccessFiles,
    refetch: refetchFiles,
  } = useGetKnowledgeBaseFilesQuery(
    { id: knowledgeBaseId, sync: false },
    { skip: !knowledgeBaseId || !collectorsResponse },
  );

  const [
    getLazyKnowledgeBaseFilesTrigger,
    { data: lazyFilesResponse, isFetching: isLazyFetchingFiles },
  ] = useLazyGetKnowledgeBaseFilesQuery();
  const getLazyKnowledgeBaseFiles = (args: LazyFilesArgs) => getLazyKnowledgeBaseFilesTrigger(args);

  const [fetchFileByIdTrigger] = useLazyGetKnowledgeBaseFileByIdQuery();

  const [addKnowledgeBaseFile] = useAddKnowledgeBaseFileMutation();
  const [addKnowledgeBaseDocument, { isSuccess: isSuccessAddKnowledgeBaseDocument }] =
    useAddKnowledgeBaseDocumentMutation();
  const [deleteKnowledgeBaseFiles] = useDeleteKnowledgeBaseFilesMutation();
  const [updateKnowledgeBaseFiles, { isSuccess: isSuccessUpdateKnowledgeBaseFiles }] =
    useUpdateKnowledgeBaseFilesMutation();
  const [updateKnowledgeBaseFile, { isSuccess: isSuccessUpdateKnowledgeBaseFile }] =
    useUpdateKnowledgeBaseFileMutation();

  useEffect(() => {
    const newFiles: KBFile[] = [];

    if (!skipRefetchFiles && filesResponse) {
      newFiles.push(...filesResponse);
    }

    if (!skipRefetchFiles && lazyFilesResponse) {
      newFiles.push(...lazyFilesResponse);
    }

    setFiles(newFiles);
  }, [filesResponse, lazyFilesResponse, skipRefetchFiles]);

  useEffect(() => {
    if (collectorsResponse) {
      setIntegrations(collectorsResponse);
    }
  }, [collectorsResponse]);

  const isActionButtonsDisabled = files.some(
    (file) => file.state === FILE_STATE.DELETING || file.state === FILE_STATE.UPLOADING,
  );

  useEffect(() => {
    if (skipRefetchFiles || !isActionButtonsDisabled) {
      return;
    }

    const interval = setInterval(() => {
      refetchFiles();
    }, 3000);

    return () => clearInterval(interval);
  }, [isActionButtonsDisabled, skipRefetchFiles]);

  useEffect(() => {
    if (isSuccessAddKnowledgeBaseDocument) {
      notification.success('New Document Added Successfully.');
    }
  }, [isSuccessAddKnowledgeBaseDocument]);

  useEffect(() => {
    if (isSuccessUpdateKnowledgeBaseFiles) {
      notification.success('Updated Successfully.');
    }
  }, [isSuccessUpdateKnowledgeBaseFiles]);

  useEffect(() => {
    if (isSuccessUpdateKnowledgeBaseFile) {
      notification.success('Updated Successfully.');
    }
  }, [isSuccessUpdateKnowledgeBaseFile]);

  const refetchFilesAndSync = async () => {
    const result = await refetchFiles();

    if (result?.data) {
      setFiles([...(result.data || [])]);
    }

    return result;
  };

  const fetchFileById = async (fileId: string): Promise<FileDownloadDetails> => {
    const res = await fetchFileByIdTrigger({ id: knowledgeBaseId, file_id: fileId });

    return {
      filename: res.data.name,
      url: res.data.download_url,
      path: res.data.path || '/',
    };
  };

  return {
    currentKnowledgeBase,
    files,
    integrations,
    isLoadingFiles,
    isLazyFetchingFiles,
    isLoadingCollectors,
    isSuccessFiles,
    isActionButtonsDisabled,
    isCosupportManager,
    isV1,
    refetchFilesAndSync,
    getLazyKnowledgeBaseFiles,
    fetchFileById,
    addKnowledgeBaseFile,
    addKnowledgeBaseDocument,
    deleteKnowledgeBaseFiles,
    updateKnowledgeBaseFile,
    updateKnowledgeBaseFiles,
  };
};
